/**
 * AI App Data Context - Utility to gather currently loaded app data for AI
 * This ensures AI references the same data users are currently seeing in their apps
 */

import { remindersManager } from './remindersData';

export const aiAppDataContext = {

  /**
   * Get all cached app data to send with AI requests
   * @returns {Object} - Formatted data from all apps currently loaded
   */
  getAllAppData() {
    return {
      charting: this.getChartingData(),
      socialListening: this.getSocialListeningData(),
      playlisting: this.getPlaylistingData(),
      spotifyRevenue: this.getSpotifyRevenueData(),
      reminders: this.getRemindersData()
    };
  },

  /**
   * Get cached charting data from localStorage (same as ChartingApp uses)
   */
  getChartingData() {
    try {
      const cachedData = localStorage.getItem('charting_app_data');
      if (!cachedData) return null;

      const parsed = JSON.parse(cachedData);
      console.log('📊 AI Context: Found cached charting data');
      
      return {
        chartData: parsed.chartData || [],
        summaryData: parsed.summaryData || { total_artists: 0, total_activities: 0 },
        catalogArtists: parsed.catalogArtists || [],
        timestamp: parsed.timestamp
      };
    } catch (error) {
      console.error('❌ Error reading charting cache for AI:', error);
      return null;
    }
  },

  /**
   * Get cached social listening data from localStorage (same as SocialListeningApp uses)
   */
  getSocialListeningData() {
    try {
      const cachedData = localStorage.getItem('social_listening_app_data');
      if (!cachedData) return null;

      const parsed = JSON.parse(cachedData);
      console.log('💬 AI Context: Found cached social listening data');
      
      return {
        socialData: parsed.socialData || [],
        summaryData: parsed.summaryData || { total_artists: 0, total_activities: 0 },
        catalogArtists: parsed.catalogArtists || [],
        timestamp: parsed.timestamp
      };
    } catch (error) {
      console.error('❌ Error reading social listening cache for AI:', error);
      return null;
    }
  },

  /**
   * Get playlisting data from localStorage cache (if cached) or indicate database source
   */
  getPlaylistingData() {
    try {
      // Check if playlisting app has cached data
      const getCacheKey = () => `playlist_discoveries_${localStorage.getItem('selectedArtist') || 'default'}`;
      const cachedData = localStorage.getItem(getCacheKey());
      
      if (cachedData) {
        const parsed = JSON.parse(cachedData);
        console.log('🎵 AI Context: Found cached playlisting data');
        
        return {
          discoveryHistory: parsed.discoveries || [],
          timestamp: parsed.timestamp,
          source: 'localStorage_cache'
        };
      }

      // Indicate data would come from database
      return {
        note: "Playlisting data stored in database via 'user/playlist-discoveries' endpoint",
        source: 'database'
      };
    } catch (error) {
      console.error('❌ Error reading playlisting cache for AI:', error);
      return null;
    }
  },

  /**
   * Get Spotify Revenue data (currently mock data in the app)
   */
  getSpotifyRevenueData() {
    // Since SpotifyRevenueApp currently uses mock data, return indication of that
    return {
      note: "Spotify Revenue currently uses mock data in the app",
      source: 'mock_data'
    };
  },

  /**
   * Get reminders data from the reminders manager
   */
  getRemindersData() {
    try {
      const context = remindersManager.generateAIContext();
      console.log('⏰ AI Context: Found reminders data');
      return context;
    } catch (error) {
      console.error('❌ Error reading reminders data for AI:', error);
      return null;
    }
  },

  /**
   * Format app data into AI-readable context strings
   * @param {Object} appData - Data from getAllAppData()
   * @returns {Object} - Formatted context strings for each app
   */
  formatForAI(appData) {
    const contexts = {};

    // Format Charting Context
    if (appData.charting && appData.charting.chartData) {
      let chartContext = `\n=== CHARTING DATA (Currently Loaded in App) ===\n`;
      const summary = appData.charting.summaryData;
      chartContext += `- Total Artists: ${summary.total_artists}\n`;
      chartContext += `- Total Activities: ${summary.total_activities}\n`;
      
      if (appData.charting.chartData.length > 0) {
        chartContext += `\nRECENT CHART ACTIVITIES:\n`;
        appData.charting.chartData.slice(0, 5).forEach((activity, index) => {
          chartContext += `${index + 1}. ${activity.track_name || 'Unknown Track'} by ${activity.artist_name || 'Unknown Artist'}\n`;
          if (activity.chart_name) chartContext += `   Chart: ${activity.chart_name}\n`;
          if (activity.position) chartContext += `   Position: #${activity.position}\n`;
        });
      }
      contexts.charting = chartContext;
    }

    // Format Social Listening Context  
    if (appData.socialListening && appData.socialListening.socialData) {
      let socialContext = `\n=== SOCIAL LISTENING DATA (Currently Loaded in App) ===\n`;
      const summary = appData.socialListening.summaryData;
      socialContext += `- Total Artists: ${summary.total_artists}\n`;
      socialContext += `- Total Activities: ${summary.total_activities}\n`;
      
      if (appData.socialListening.socialData.length > 0) {
        socialContext += `\nRECENT SOCIAL MENTIONS:\n`;
        appData.socialListening.socialData.slice(0, 5).forEach((mention, index) => {
          socialContext += `${index + 1}. ${mention.track_name || 'Unknown Track'} mentioned on ${mention.platform || 'social media'}\n`;
          if (mention.curator_name) socialContext += `   By: ${mention.curator_name}\n`;
          if (mention.content) socialContext += `   Content: ${mention.content.substring(0, 60)}...\n`;
        });
      }
      contexts.socialListening = socialContext;
    }

    // Format Playlisting Context
    if (appData.playlisting) {
      if (appData.playlisting.discoveryHistory && appData.playlisting.discoveryHistory.length > 0) {
        let playlistContext = `\n=== PLAYLISTING DATA (Currently Loaded in App) ===\n`;
        playlistContext += `- Total Discoveries: ${appData.playlisting.discoveryHistory.length}\n`;
        
        const allPlaylists = appData.playlisting.discoveryHistory.flatMap(d => d.playlists || []);
        playlistContext += `- Total Playlist Placements: ${allPlaylists.length}\n`;
        
        if (allPlaylists.length > 0) {
          playlistContext += `\nRECENT PLAYLIST PLACEMENTS:\n`;
          allPlaylists.slice(0, 5).forEach((playlist, index) => {
            playlistContext += `${index + 1}. "${playlist.playlist_name}" (${playlist.followers?.toLocaleString() || 'N/A'} followers)\n`;
          });
        }
        contexts.playlisting = playlistContext;
      } else {
        contexts.playlisting = `\n=== PLAYLISTING DATA ===\nNo playlist discoveries currently loaded in app\n`;
      }
    }

    // Format Spotify Revenue Context
    if (appData.spotifyRevenue) {
      contexts.spotifyRevenue = `\n=== SPOTIFY REVENUE DATA ===\n${appData.spotifyRevenue.note}\n`;
    }

    // Format Reminders Context
    if (appData.reminders) {
      let remindersContext = `\n=== REMINDERS DATA (Active Release Workflow) ===\n`;
      remindersContext += `- Total Active Reminders: ${appData.reminders.total_active || 0}\n`;
      remindersContext += `- Overdue Reminders: ${appData.reminders.overdue_count || 0}\n`;
      remindersContext += `- Due Today: ${appData.reminders.due_today || 0}\n`;
      remindersContext += `- Upcoming (Next 7 days): ${appData.reminders.upcoming_week || 0}\n`;
      remindersContext += `- Unviewed: ${appData.reminders.unviewed_count || 0}\n`;
      remindersContext += `- Critical: ${appData.reminders.critical_count || 0}\n`;
      
      if (appData.reminders.active_reminders && appData.reminders.active_reminders.length > 0) {
        remindersContext += `\nACTIVE REMINDERS:\n`;
        appData.reminders.active_reminders.slice(0, 5).forEach((reminder, index) => {
          remindersContext += `${index + 1}. ${reminder.title} - ${reminder.release_name}\n`;
          remindersContext += `   Due: ${reminder.days_until_due > 0 ? `${reminder.days_until_due} days` : reminder.is_overdue ? 'OVERDUE' : 'Today'}\n`;
          remindersContext += `   Priority: ${reminder.priority || 'medium'}\n`;
          remindersContext += `   Category: ${reminder.category || 'general'}\n`;
        });
      }
      contexts.reminders = remindersContext;
    }

    return contexts;
  },

  /**
   * Get complete formatted context for AI requests
   * @returns {string} - Complete context string with all loaded app data
   */
  getFormattedContext() {
    const appData = this.getAllAppData();
    const contexts = this.formatForAI(appData);
    
    // Combine all contexts
    const contextString = Object.values(contexts).join('\n');
    
    console.log('🤖 AI Context: Prepared app data context');
    return contextString || '\n=== APP DATA ===\nNo app data currently loaded\n';
  },

  /**
   * Check what apps have data currently loaded
   * @returns {Object} - Status of each app's data
   */
  getDataStatus() {
    const appData = this.getAllAppData();
    return {
      charting: !!(appData.charting && appData.charting.chartData?.length > 0),
      socialListening: !!(appData.socialListening && appData.socialListening.socialData?.length > 0),
      playlisting: !!(appData.playlisting && appData.playlisting.discoveryHistory?.length > 0),
      spotifyRevenue: false, // Currently mock data
      reminders: !!(appData.reminders && appData.reminders.total_active > 0)
    };
  }

};

export default aiAppDataContext; 